import {
  ConstructionInhomogeneousLayer,
  ConstructionMaterial,
  UpsertConstruction,
} from "@/types/Views/CreateConstruction/NewConstruction";
import CalculatorConfigs from "./constants/CalculatorConfigs";
import {
  ConstructionAmountResult,
  ConstructionMaterialCalculateInput,
  FromUnitInput,
  InputField,
  InputFieldType,
  ToUnitInput,
} from "./types";
import getConfig from "./utils/getConfig";
import validateResult from "./utils/validateResult";

export const DefaultValue: ConstructionAmountResult = {
  amount: 0,
  dimensions: {},
  error: true,
  weight: 0,
};

class ConstructionAmountCalculator {
  private config: InputField | undefined;
  private fromUnitInput: FromUnitInput | undefined;
  private toUnitInput: ToUnitInput | undefined;

  private hasValidInput(): boolean {
    return [this.fromUnitInput, this.toUnitInput, this.config].every((input) => input !== undefined);
  }

  public getConfig(): InputField | undefined {
    return this.config;
  }

  public getFromUnitInput(): FromUnitInput | undefined {
    return this.fromUnitInput;
  }

  public getToUnitInput(): ToUnitInput | undefined {
    return this.toUnitInput;
  }

  public getFromCubicMeters(constructionMaterial: ConstructionMaterialCalculateInput, amount: number): number {
    if (this.hasValidInput() && this.config?.calculate.getFromCubicMeters) {
      return this.config.calculate.getFromCubicMeters(this.fromUnitInput!, this.toUnitInput!, constructionMaterial, amount);
    }

    return 0;
  }

  public getToCubicMeters(constructionMaterial: ConstructionMaterialCalculateInput): number {
    if (this.hasValidInput() && this.config?.calculate.getToCubicMeters) {
      return (
        this.config.calculate.getToCubicMeters(this.fromUnitInput!, this.toUnitInput!, constructionMaterial) *
        (this.fromUnitInput?.amount ?? 0)
      );
    }

    return 0;
  }

  public byDimensions(input: ConstructionMaterialCalculateInput): ConstructionAmountResult {
    if (this.hasValidInput() && this.config?.calculate?.byDimensions) {
      return validateResult(this.config.calculate.byDimensions(this.fromUnitInput!, this.toUnitInput!, input));
    }

    return DefaultValue;
  }

  public byInhomogeneousLayerThickness(constructionMaterial: ConstructionMaterial): ConstructionAmountResult {
    this.toConstructionMaterial(constructionMaterial);

    if (this.hasValidInput()) {
      if (this.config?.calculate?.byInhomogeneousLayerThickness) {
        return validateResult(
          this.config.calculate.byInhomogeneousLayerThickness(this.fromUnitInput!, this.toUnitInput!, constructionMaterial)
        );
      }

      return {
        amount: constructionMaterial.amount,
        dimensions: {
          height: constructionMaterial.height,
          length: constructionMaterial.length,
          thickness: constructionMaterial.thickness,
        },
        weight: constructionMaterial.weight ?? 0,
        error: false,
      };
    }

    return DefaultValue;
  }

  public byQuantity(constructionMaterial: ConstructionMaterialCalculateInput): ConstructionAmountResult {
    if (this.hasValidInput() && this.config?.calculate?.byQuantity) {
      return validateResult(this.config.calculate.byQuantity(this.fromUnitInput!, this.toUnitInput!, constructionMaterial));
    }

    return DefaultValue;
  }

  public byWeight(input: ConstructionMaterialCalculateInput): ConstructionAmountResult {
    if (this.hasValidInput() && this.config?.calculate?.byWeight) {
      return validateResult(this.config.calculate.byWeight(this.fromUnitInput!, this.toUnitInput!, input));
    }

    return DefaultValue;
  }

  public from(input: FromUnitInput): ConstructionAmountCalculator {
    const formatedInput: FromUnitInput = {
      ...input,
      declaredUnitId: parseInt(input.declaredUnitId + ""),
    };
    const result = new ConstructionAmountCalculator();
    result.config = this.toUnitInput !== undefined ? getConfig(CalculatorConfigs, formatedInput, this.toUnitInput) : undefined;
    result.fromUnitInput = formatedInput;

    return result;
  }

  public fromConstruction(construction: UpsertConstruction): ConstructionAmountCalculator {
    const newConstructionDeclaredUnitId = construction.declaredUnit.unitId;
    const declaredUnitId = parseInt(newConstructionDeclaredUnitId + "");

    const fromUnit: FromUnitInput = {
      amount: 1,
      declaredUnitId,
      isInhomogeneousLayer: false,
    };

    return this.from(fromUnit);
  }

  public fromInhomogeneousLayer(
    inhomogeneousLayer: ConstructionInhomogeneousLayer,
    declaredUnitId: number
  ): ConstructionAmountCalculator {
    const formatedDeclaredUnitId = parseInt(declaredUnitId + "");

    const fromUnit: FromUnitInput = {
      amount: inhomogeneousLayer.amount || 1,
      declaredUnitId: formatedDeclaredUnitId,
      isInhomogeneousLayer: true,
    };

    return this.from(fromUnit);
  }

  public to(input: ToUnitInput): this {
    this.config = this.fromUnitInput !== undefined ? getConfig(CalculatorConfigs, this.fromUnitInput, input) : undefined;
    this.toUnitInput = input;

    return this;
  }

  public toConstructionMaterial(newConstructionMaterial: ConstructionMaterial): this {
    const newDeclaredUnit = newConstructionMaterial.material.declaredUnit;

    const newWeightPerDeclaredUnit = newConstructionMaterial.material.weightPerDeclaredUnit;

    const massUnit = {
      numeratorUnitId: newConstructionMaterial.material.weightPerDeclaredUnit.unit.id,
      denominatorUnitId: newConstructionMaterial.material.weightPerDeclaredUnit.denominatorUnit?.id,
    };

    const toUnit = {
      declaredUnitId: newDeclaredUnit.unit.id,
      declaredValue: newDeclaredUnit.value,
      massUnit: massUnit,
      massValue: newWeightPerDeclaredUnit.value,
    };

    return this.to(toUnit);
  }

  public inputFields(): Array<InputFieldType> {
    return this.config?.fields ?? [];
  }

  public validate(constructionMaterial: ConstructionMaterial): boolean {
    return this.calculate(constructionMaterial).amount > 0;
  }

  public calculate(constructionMaterial: ConstructionMaterial): ConstructionAmountResult {
    this.toConstructionMaterial(constructionMaterial);

    if (this.hasValidInput()) {
      if (this.config?.calculate.onLoad) {
        return validateResult(this.config.calculate.onLoad(this.fromUnitInput!, this.toUnitInput!, constructionMaterial));
      }
    }

    return DefaultValue;
  }
}

export default ConstructionAmountCalculator;
